import { $$ } from '../utils.js';
import { CONFIG } from '../config.js';

// Custom Carousel Class
class CustomCarousel {
  constructor(element) {
    this.element = element;
    this.isRunning = false;
    this.isPaused = false;
    this.animationId = null;
    this.startTime = null;
    this.pausedTime = 0;
    this.speed = CONFIG.animation.carouselSpeed;
    this.isHovered = false;
    this.currentPosition = 0;
    
    this.init();
  }
  
  init() {
    this.setupStructure();
    this.setupHoverEvents();
    
    // Start in paused state to establish layout
    this.start();
    this.pause();
  }
  
  setupStructure() {
    // Only remove Splide classes, don't modify any Webflow styling
    this.element.classList.remove('splide__list');
    const items = this.element.querySelectorAll('.splide__slide');
    items.forEach(item => {
      item.classList.remove('splide__slide');
    });
    
    // Only add essential animation properties, preserve all Webflow CSS
    this.element.style.willChange = 'transform';
    this.element.style.transition = 'none';
    
    // Clone items multiple times for smoother infinite loop
    const originalItems = Array.from(this.element.children);
    
    // Clone items twice to ensure smooth scrolling
    for (let i = 0; i < 2; i++) {
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        this.element.appendChild(clone);
      });
    }
    
    // Calculate dimensions
    this.updateDimensions();
    
    // Set initial position
    this.element.style.transform = `translateX(0px)`;
  }
  
  updateDimensions() {
    // Calculate the width of one set of original items
    const originalItems = Array.from(this.element.children);
    const itemCount = originalItems.length / 3; // We have 3 sets now (original + 2 clones)
    let totalWidth = 0;
    
    // Calculate width of one complete set
    for (let i = 0; i < itemCount; i++) {
      totalWidth += originalItems[i].offsetWidth;
      // Add gap between items if exists
      const computedStyle = window.getComputedStyle(originalItems[i]);
      const marginRight = parseFloat(computedStyle.marginRight) || 0;
      const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
      totalWidth += marginRight + marginLeft;
    }
    
    this.loopWidth = totalWidth;
  }
  
  setupHoverEvents() {
    const container = this.element.closest('.hero-logos-wrapper') || 
                    this.element.closest('.hero-logo-slider-holder') ||
                    this.element.closest('#logo-brand');
    
    if (!container) return;
    
    container.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.pause();
    });
    
    container.addEventListener('mouseleave', () => {
      this.isHovered = false;
      if (this.isRunning) {
        this.resume();
      }
    });
  }
  
  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    this.startTime = performance.now();
    this.pausedTime = 0;
    this.animate();
  }
  
  pause() {
    this.isPaused = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  resume() {
    if (!this.isRunning || !this.isPaused || this.isHovered) return;
    
    this.isPaused = false;
    this.startTime = performance.now() - this.pausedTime;
    this.animate();
  }
  
  animate() {
    if (this.isPaused) return;
    
    const currentTime = performance.now();
    const elapsed = currentTime - this.startTime;
    
    // Calculate smooth continuous position
    this.currentPosition = (elapsed * this.speed * 0.001);
    
    // When we've scrolled past one complete set, reset seamlessly
    if (this.currentPosition >= this.loopWidth) {
      this.currentPosition = this.currentPosition % this.loopWidth;
      this.startTime = currentTime - (this.currentPosition / (this.speed * 0.001));
    }
    
    // Apply transform with subpixel precision
    this.element.style.transform = `translate3d(${-this.currentPosition}px, 0, 0)`;
    
    // Store paused time for smooth resume
    this.pausedTime = elapsed;
    
    if (this.isRunning && !this.isPaused) {
      this.animationId = requestAnimationFrame(() => this.animate());
    }
  }
  
  destroy() {
    this.pause();
    this.isRunning = false;
    
    // Remove cloned items
    const items = Array.from(this.element.children);
    const originalCount = items.length / 3; // We have 3 sets now
    items.slice(originalCount).forEach(item => item.remove());
    
    // Reset styles
    this.element.style.transform = '';
    this.element.style.willChange = '';
    this.element.style.transition = '';
  }
}

// Logo Carousel Module - Compatible with existing API
export const MarqueeModule = {
  instances: [],
  
  init() {
    // Find containers using querySelectorAll pattern
    const containers = $$('.hero-logo-slider-holder, #logo-brand');
    containers.forEach(container => {
      const logoList = container.querySelector('.hero-logo-list');
      if (!logoList) return;
      
      const carousel = new CustomCarousel(logoList);
      this.instances.push(carousel);
    });
  },
  
  startAll() {
    this.instances.forEach(instance => instance.start());
  },
  
  pauseAll() {
    this.instances.forEach(instance => instance.pause());
  },
  
  resumeAll() {
    this.instances.forEach(instance => instance.resume());
  },
  
  destroy() {
    this.instances.forEach(instance => instance.destroy());
    this.instances = [];
  }
};